/**
 * Models Catalog
 * Primary IoT Shack view: device types and reusable models
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createDevice, fetchFleetSummary, fetchModels } from './api';
import { catalogCountHeadline, normalizeCatalogInstanceCounts } from './catalogCountHonesty';
import type { DeviceModel } from './types';

export default function ModelsCatalog() {
  const { data: models = [], isLoading, error } = useQuery({
    queryKey: ['iot', 'models'],
    queryFn: fetchModels,
    refetchInterval: 20000,
  });

  const fleetQuery = useQuery({
    queryKey: ['iot', 'fleet-summary'],
    queryFn: fetchFleetSummary,
    refetchInterval: 15000,
    retry: false,
  });
  const fleetDeviceTotal = fleetQuery.isError
    ? null
    : fleetQuery.data?.total_devices ?? null;

  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return models;
    const q = query.toLowerCase();
    return models.filter((model) => {
      return (
        model.name?.toLowerCase().includes(q) ||
        model.manufacturer?.toLowerCase().includes(q) ||
        model.model?.toLowerCase().includes(q) ||
        model.type?.toLowerCase().includes(q)
      );
    });
  }, [models, query]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, model) => {
      const key = model.type || 'unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(model);
      return acc;
    }, {} as Record<string, DeviceModel[]>);
  }, [filtered]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load models: {String(error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sensor Models (Generic)</h1>
          <p className="text-gray-600 mt-1">
            {models.length} models • reusable configs
            {fleetDeviceTotal === 0
              ? ' • fleet is empty, cards are templates not devices'
              : ', not specific IMEIs'}
          </p>
        </div>
        <Link
          to="/iotshack/agent"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium shadow-sm hover:shadow-md hover:from-blue-500 hover:to-indigo-500"
        >
          🤖 Onboard New Sensor
        </Link>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between shadow-sm">
        <div className="flex flex-1 items-center gap-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by model, manufacturer, or type..."
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="text-xs text-gray-500">
          Select a model to view its instances (IMEIs)
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Models Found</h3>
          <p className="text-gray-600">Use the IoT Agent to onboard a sensor model</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type} className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {type}
                </h2>
                <span className="text-xs text-gray-500">{items.length} models</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((model) => (
                  <ModelCard
                    key={model.id}
                    model={model}
                    fleetDeviceTotal={fleetDeviceTotal}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ModelCard({
  model,
  fleetDeviceTotal,
}: {
  model: DeviceModel;
  fleetDeviceTotal: number | null;
}) {
  const counts = normalizeCatalogInstanceCounts(model.instances, {
    source: model.source,
    fleetDeviceTotal,
  });
  const modelRef = model.ref || model.slug || model.id;
  const [showAttach, setShowAttach] = useState(false);
  const [imei, setImei] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const attachMutation = useMutation({
    mutationFn: async () => {
      if (!imei.trim()) throw new Error('IMEI is required');
      return createDevice({
        deviceId: imei.trim(),
        type: model.type || 'telemetry_sensor',
        templateId: model.deviceTemplateId || null,
        gatewayId: gatewayId.trim() || null,
        metadata: {
          imei: imei.trim(),
          manufacturer: model.manufacturer || null,
          model: model.model || null,
          model_product_id: model.id || null,
          model_id: model.id || null,
        },
      });
    },
    onSuccess: () => {
      setImei('');
      setGatewayId('');
      setShowAttach(false);
    },
  });
  return (
    <div className="bg-gradient-to-br from-white to-indigo-50/30 rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={`/iotshack/models/${modelRef}`}
            className="text-sm font-semibold text-blue-700 hover:text-blue-800"
          >
            {model.name}
          </Link>
          <p className="text-xs text-gray-500 mt-1">
            {(model.manufacturer || 'Unknown')} • {(model.model || 'Unknown')}
          </p>
        </div>
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          {counts.kind === 'template'
            ? 'Template'
            : model.source === 'derived'
              ? 'Derived'
              : 'PIM'}
        </span>
      </div>

      {counts.kind === 'template' ? (
        <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 text-center">
          <div className="text-xs font-semibold text-indigo-900">Model / template</div>
          <div className="text-xs text-gray-600 mt-1">{catalogCountHeadline(counts)}</div>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-4 gap-2 text-xs text-gray-600">
          <div className="rounded-lg border border-gray-100 bg-white/70 p-2 text-center">
            <div className="text-gray-900 font-semibold">{counts.total}</div>
            <div>Total</div>
          </div>
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-2 text-center">
            <div className="text-emerald-700 font-semibold">{counts.online}</div>
            <div>Online</div>
          </div>
          <div className="rounded-lg border border-rose-100 bg-rose-50/60 p-2 text-center">
            <div className="text-rose-700 font-semibold">{counts.offline}</div>
            <div>Offline</div>
          </div>
          <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-2 text-center">
            <div className="text-amber-800 font-semibold">{counts.unknown}</div>
            <div>Unknown</div>
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        Template: {model.templateId || '—'}
      </div>

      <div className="mt-3">
        <button
          onClick={() => setShowAttach((v) => !v)}
          className="text-xs text-blue-700 hover:text-blue-800"
        >
          {showAttach ? 'Hide IMEI attach' : 'Attach IMEI'}
        </button>
        {showAttach && (
          <div className="mt-2 space-y-2">
            <input
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              placeholder="IMEI (device identifier)"
              className="w-full px-3 py-2 border border-gray-300 rounded text-xs"
            />
            <input
              value={gatewayId}
              onChange={(e) => setGatewayId(e.target.value)}
              placeholder="Gateway ID (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded text-xs"
            />
            <button
              onClick={() => attachMutation.mutate()}
              disabled={!imei.trim() || attachMutation.isPending}
              className="w-full px-3 py-2 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300"
            >
              {attachMutation.isPending ? 'Attaching…' : 'Attach IMEI'}
            </button>
            {attachMutation.isError && (
              <div className="text-xs text-red-600">{String(attachMutation.error)}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
